import { actionTree, getterTree, mutationTree } from 'typed-vuex';
import { GpxTrack } from '@/lib/models/gpxTrack';

/**
 * The gpxTracks module's state
 */
export const state = () => ({
  /**
   * The stored GPX tracks
   */
  data: [] as GpxTrack[],

  /**
   * The selected GPX track's id.
   */
  selectedGpxTrackId: undefined as string | undefined,
});

export const getters = getterTree(state, {
  allGpxTracks(state): GpxTrack[] {
    return state.data;
  },

  selectedGpxTrack(state): GpxTrack | undefined {
    return state.data.find(
      (gpxTrack) => gpxTrack.id === state.selectedGpxTrackId
    );
  },
});

/**
 * The key for storing GPX tracks to localStorage.
 */
export const GPX_TRACKS_STORAGE_KEY = 'gpxTracks';

/**
 * The gpxTrack module's mutations.
 */
export const mutations = mutationTree(state, {
  /**
   * Set the GPX tracks.
   *
   * @param gpxTracks - The GPX tracks to set.
   */
  SET_GPX_TRACKS(state, gpxTracks: GpxTrack[]): void {
    state.data = gpxTracks;
    localStorage.setItem(GPX_TRACKS_STORAGE_KEY, JSON.stringify(state.data));
  },

  /**
   * Set the selected GPX track id.
   *
   * @param gpxTrackId - The newly selected GPX track's id.
   */
  SET_SELECTED_GPX_TRACK_ID(state, gpxTrackId: string | undefined): void {
    state.selectedGpxTrackId = gpxTrackId;
  },

  /**
   * Set visiblity of a GPX track.
   *
   * @param id - ID of the GPX track.
   * @constructor
   */
  TOGGLE_GPX_TRACK_VISIBLE(state, id: string): void {
    const gpxTrack = state.data.find((x) => x.id === id);
    if (gpxTrack) {
      gpxTrack.visible = !gpxTrack.visible;
      localStorage.setItem(GPX_TRACKS_STORAGE_KEY, JSON.stringify(state.data));
    }
  },
});

/**
 * The gpxTracks module's actions.
 */
export const actions = actionTree(
  { state, getters, mutations },
  {
    /**
     * Update/add a GPX track.
     *
     * @param gpxTrack - The GPX track to update.
     */
    updateGpxTracks({ commit, state }, gpxTrack: GpxTrack): void {
      commit('SET_GPX_TRACKS', [
        ...state.data.filter((x) => x.id !== gpxTrack.id),
        gpxTrack,
      ]);
    },

    /**
     * Set the GPX tracks.
     *
     * @param gpxTracks - The GPX tracks to set.
     */
    setGpxTracks({ commit }, gpxTracks: GpxTrack[]): void {
      commit('SET_GPX_TRACKS', gpxTracks);
    },

    /**
     * Remove a gpxTrack.
     *
     * @param gpxTrack - The gpxTrack to remove.
     */
    removeGpxTrack({ commit, state }, gpxTrack: GpxTrack): void {
      commit('SET_GPX_TRACKS', [
        ...state.data.filter((x) => x.id !== gpxTrack.id),
      ]);
    },

    toggleVisibleGpxTrack({ commit }, gpxTrack: GpxTrack): void {
      commit('TOGGLE_GPX_TRACK_VISIBLE', gpxTrack.id);
    },

    /**
     * Clear the GPX tracks.
     */
    clearGpxTracks({ commit }): void {
      commit('SET_GPX_TRACKS', []);
      commit('SET_SELECTED_GPX_TRACK_ID', undefined);
    },

    /**
     * Set the selected GPX track id.
     *
     * @param gpxTrackId - The id of the GPX track to select.
     */
    setSelectedGpxTrackId({ commit }, gpxTrackId: string | undefined): void {
      commit('SET_SELECTED_GPX_TRACK_ID', gpxTrackId);
    },
  }
);

/**
 * The GPX tracks module.
 */
export const gpxTracks = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
